import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { GetStartedDialog } from '@/components/landing/GetStartedDialog';
import { handleHashLinkClick } from '@/lib/hashNav';
import { ArrowRight, ArrowUpRight, Menu } from 'lucide-react';
import loopAsset from '@/assets/culturelens-loop.png.asset.json';
import confidentialityAsset from '@/assets/culturelens-confidentiality.png.asset.json';


/* -------------------------------------------------------------- */
/*  Reveal                                                        */
/* -------------------------------------------------------------- */

function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            (e.target as HTMLElement).classList.add('is-visible');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Nav                                                           */
/* -------------------------------------------------------------- */

import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { usePageMeta } from '@/hooks/usePageMeta';

function Logo() {
  return (
    <Link to="/" className="flex items-center group shrink-0" aria-label="CultureLens home">
      <span className="font-display text-[18px] font-normal tracking-[0.18em] text-ink/80 group-hover:text-ink transition-colors">
        IV
      </span>
    </Link>
  );
}






/* -------------------------------------------------------------- */
/*  Product artifacts                                             */
/* -------------------------------------------------------------- */



/* OperationalFlow removed: duplicated HowItWorks. */


/* -------------------------------------------------------------- */
/*  Sections                                                      */
/* -------------------------------------------------------------- */



function Hero() {
  return (
    <section className="relative pt-32 pb-20 md:pt-36 md:pb-28 border-b border-hairline">
      <div className="max-w-content mx-auto px-6 sm:px-10">
        <div>
          <Reveal delay={80}>
            <h1 className="font-display !font-bold text-[clamp(44px,9.2vw,112px)] leading-[1.0] tracking-[-0.03em] text-ink">
              You find out your best people are leaving after they have gone.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-8 max-w-[60ch] text-[18px] md:text-[20px] text-charcoal/80 font-grotesk leading-[1.6]">
              Surveys ask intermittently.{' '}
              <em className="not-italic text-iv-orange font-medium">CultureLens</em> listens every day.
            </p>
          </Reveal>

          <Reveal delay={220}>
            <div className="mt-9 flex flex-wrap items-center gap-5">
              <GetStartedDialog>
                <Button className="group bg-ink hover:bg-ink-soft text-cream rounded-full h-12 px-6 font-grotesk text-[15px] font-medium hover:-translate-y-px transition-transform duration-300">
                  Request access <ArrowRight className="ml-2 h-4 w-4 cta-arrow" />
                </Button>
              </GetStartedDialog>
              <Link
                to="/who-we-are#contact"
                onClick={handleHashLinkClick('/who-we-are', '#contact')}
                className="font-grotesk text-[14px] text-charcoal hover:text-ink transition-colors inline-flex items-center gap-1 underline underline-offset-4"
              >
                Talk to an expert <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}




function TrustArchitecture() {
  return (
    <section id="trust" className="bg-cream py-24 md:py-32 border-b border-hairline">
      <div className="max-w-content mx-auto px-6 sm:px-10">
        <Reveal>
          <div className="font-grotesk text-[12px] tracking-[0.16em] uppercase text-charcoal/60">
            Confidentiality
          </div>
        </Reveal>



        <Reveal delay={120}>
          <div className="mt-8 md:mt-10 bg-cream isolate">
            <figure className="mx-auto w-full max-w-[880px]">
              <img
                src={confidentialityAsset.url}
                alt="Confidential by design, aggregated by default: people speak freely, nobody sees who said what, nothing resolves to fewer than five people, aggregated insights drive action."
                loading="lazy"
                className="mx-auto block w-full h-auto mix-blend-multiply"
              />
            </figure>

          </div>
        </Reveal>


      </div>
    </section>
  );
}





function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-cream py-24 md:py-32 border-b border-hairline">
      <div className="max-w-content mx-auto px-6 sm:px-10">
        <Reveal>
          <div className="font-grotesk text-[12px] tracking-[0.16em] uppercase text-charcoal/60">
            How it works
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-12 md:mt-14 border-t border-hairline pt-12 md:pt-16 bg-cream isolate">
            <figure className="mx-auto w-full max-w-[880px]">
              <img
                src={loopAsset.url}
                alt="How CultureLens works: people talk, patterns emerge, you see what changed, impact compounds and the cycle repeats."
                loading="lazy"
                className="mx-auto block w-full h-auto mix-blend-multiply"
              />
              <figcaption className="mt-8 md:mt-10 flex flex-col items-center">
                <span aria-hidden className="block h-8 w-px bg-hairline" />
                <blockquote className="mt-8 mx-auto max-w-[20ch] text-center font-display font-bold text-ink text-[clamp(30px,4.4vw,54px)] leading-[1.08] tracking-[-0.03em]">
                  &ldquo;It is built around what we needed, not a generic competency framework we had to learn.&rdquo;
                </blockquote>
              </figcaption>

            </figure>
          </div>
        </Reveal>

      </div>
    </section>
  );
}


function FinalCTA() {
  return (
    <section className="bg-cream py-28 md:py-40 border-t border-hairline">
      <div className="max-w-content mx-auto px-6 sm:px-10">
        <div className="max-w-[24ch]">
          <Reveal>
            <h2 className="font-display !font-bold text-ink text-[clamp(32px,5.2vw,60px)] leading-[1.05] tracking-[-0.025em]">
              Stop finding out too late
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-6 font-grotesk text-charcoal/70 text-[17px] md:text-[19px] leading-[1.6]">
              See what is helping performance, what is hurting it, and what to do next.
            </p>
          </Reveal>
          <Reveal delay={260}>
            <div className="mt-10">
              <GetStartedDialog>
                <Button className="group bg-ink hover:bg-ink-soft text-cream rounded-full h-12 px-7 font-grotesk text-[15px] font-medium hover:-translate-y-px transition-transform duration-300">
                  Request access <ArrowRight className="ml-2 h-4 w-4 cta-arrow" />
                </Button>
              </GetStartedDialog>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}




/* -------------------------------------------------------------- */
/*  Page                                                          */
/* -------------------------------------------------------------- */

const homeJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'CultureLens',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: 'https://culturelens.org/',
    description:
      'A platform that turns confidential employee conversations into a clear read on what is working, what is hurting performance, and what to do next.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/PreOrder',
    },
    publisher: { '@type': 'Organization', name: 'IV Talent' },
  },
];

export default function Index() {
  usePageMeta({
    title: 'CultureLens: see why people leave, before they do',
    description:
      'See what is working, what is hurting performance, and what to do next. Confidential employee conversations, aggregated so no one is identified.',
    canonical: 'https://culturelens.org/',
    jsonLd: homeJsonLd,
  });

  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <div className="bg-cream text-ink font-grotesk antialiased">
      <SiteHeader />
      <main>
        <Hero />
        <HowItWorks />
        
        <TrustArchitecture />
        <FinalCTA />
      </main>

      <SiteFooter />
    </div>
  );
}
